import React from 'react';
import { formatMonthWithYear } from '../../utils/formatters';
import { useClubSummaryData } from '../../hooks/useClubQueries';
import { ClubMetrics } from '../../types/club';
import { LoadingSpinner } from '../ui';

interface PerformanceSummaryProps {
  branchId?: number;
  brandId?: number;
  timeRange?: string;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
  selectedMonth?: string | null;
  selectedMonthData?: ClubMetrics | null;
}

const PerformanceSummary: React.FC<PerformanceSummaryProps> = ({
  branchId = 0,
  brandId,
  timeRange,
  onError,
  showLoading = true,
  compact = false,
  selectedMonth,
  selectedMonthData
}) => {
  // Use React Query hook for summary data
  const { 
    data,
    isLoading, 
    isError, 
    error 
  } = useClubSummaryData(branchId, brandId, timeRange);
  
  const summaryData = data?.summaryData;

  React.useEffect(() => {
    if (isError && onError && error) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  // Format values based on type
  const formatValue = (value: number | null, type: string) => {
    if (value === null || value === undefined) return '-';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
      case 'percent':
        return `${value.toFixed(1)}%`;
      case 'count':
        return value.toFixed(2);
      default:
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
    }
  };

  if (isLoading && showLoading) {
    return (
      <div className="card-base">
        <div className="p-6 flex items-center justify-center min-h-[200px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری…"
          />
        </div>
      </div>
    );
  }

  if (selectedMonth && selectedMonthData) {
    // Monthly Details View
    return (
      <>
        <div className="border-b border-border-primary p-6">
          <h3 className="title-card">تحلیل ماهانه</h3>
          <p className="text-text-primary text-center">{formatMonthWithYear(selectedMonth)}</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-status-info-bg rounded-lg p-4 border border-status-info">
              <p className="text-sm text-text-secondary mb-1">تعداد سفارش</p>
              <p className="text-2xl font-bold text-status-info">{selectedMonthData.invoiceCount?.toLocaleString()}</p>
            </div>
            <div className="bg-status-warning-bg rounded-lg p-4 border border-status-warning">
              <p className="text-sm text-text-secondary mb-1">اشتراک‌گیری</p>
              <p className="text-2xl font-bold text-status-warning">{selectedMonthData.subscription.toFixed(2)}%</p>
            </div>
            <div className="bg-status-success-bg rounded-lg p-4 border border-status-success">
              <p className="text-sm text-text-secondary mb-1">میانگین سفارش</p>
              <p className="text-xl font-bold text-status-success">{formatValue(selectedMonthData.avgInvoice, 'currency')}</p>
            </div>
            <div className="bg-status-info-bg rounded-lg p-4 border border-status-info">
              <p className="text-sm text-text-secondary mb-1">میانگین قیمت هر کالا</p>
              <p className="text-xl font-bold text-status-info">{formatValue(selectedMonthData.avgItemPrice, 'currency')}</p>
            </div>
          </div>

          <div className="bg-bg-secondary rounded-lg p-4 mb-4">
            <h3 className="title-card">تحلیل مشتریان و اعتبار</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-status-info-bg rounded-lg border border-status-info">
                <span className="text-sm font-medium text-text-primary">مشتریان یکتا</span>
                <span className="text-lg font-bold text-status-info">{selectedMonthData.uniqueCustomers?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-status-warning-bg rounded-lg border border-status-warning">
                <span className="text-sm font-medium text-text-primary">سفارش‌های اعتباری</span>
                <span className="text-lg font-bold text-status-warning">{selectedMonthData.creditInvoiceCount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-status-success-bg rounded-lg border border-status-success">
                <span className="text-sm font-medium text-text-primary">اعتبار مصرفی</span>
                <span className="text-lg font-bold text-status-success">{formatValue(selectedMonthData.usedCredit, 'currency')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-primary-50 rounded-lg border border-primary-200">
                <span className="text-sm font-medium text-text-primary">درصد استفاده از اعتبار</span>
                <span className="text-lg font-bold text-primary-600">{selectedMonthData.creditPercent?.toFixed(1)}%</span>
              </div>
            </div>
          </div>

          <div className="bg-bg-secondary rounded-lg p-4">
            <h3 className="title-card">آمار کلی ماه</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-status-success-bg rounded-lg border border-status-success">
                <span className="text-sm font-medium text-text-primary">مجموع فروش</span>
                <span className="text-lg font-bold text-status-success">{formatValue(selectedMonthData.totalAmount, 'currency')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-status-info-bg rounded-lg border border-status-info">
                <span className="text-sm font-medium text-text-primary">میانگین تعداد اقلام در سفارش</span>
                <span className="font-bold text-lg text-text-primary">{selectedMonthData.avgItemCount?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Summary View
  return (
    <>
      <div className="border-b border-border-primary p-6">
        <h3 className="title-card">خلاصه عملکرد</h3>
        <p className="text-text-primary text-center">تحلیل کلی باشگاه مشتریان</p>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-status-warning-bg rounded-lg p-4 border border-status-warning">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-text-primary">میانگین اشتراک‌گیری</div>
            </div>
            <div className="text-2xl font-bold text-status-warning">
              {summaryData?.avgSubscription?.toFixed(2) || '0'}%
            </div>
          </div>
          <div className="bg-status-success-bg rounded-lg p-4 border border-status-success">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-text-primary">میانگین سفارش</div>
            </div>
            <div className="text-2xl font-bold text-status-success">
              {formatValue(summaryData?.avgInvoice || 0, 'currency')}
            </div>
          </div>
          <div className="bg-status-info-bg rounded-lg p-4 border border-status-info">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-text-primary">کل سفارش‌ها</div>
            </div>
            <div className="text-2xl font-bold text-status-info">
              {summaryData?.totalInvoices?.toLocaleString() || '0'}
            </div>
          </div>
          {/* <div className="bg-status-info-bg rounded-lg p-4 border border-status-info">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-text-primary">کل مشتریان یکتا</div>
            </div>
            <div className="text-2xl font-bold text-status-info">
              {summaryData?.totalUniqueCustomers?.toLocaleString() || '0'}
            </div>
          </div> */}
        </div>

        <div className="bg-bg-secondary rounded-lg p-4 border border-border-primary text-center">
          <div className="flex items-center justify-center mb-2">
            <span className="text-2xl mr-2">💡</span>
            <span className="text-sm font-medium text-text-primary">راهنما</span>
          </div>
          <span className="text-sm text-text-primary animate-loading-pulse">
            برای مشاهده جزئیات، روی هر ماه در نقشه حرارتی کلیک کنید
          </span>
        </div>
      </div>
    </>
  );
};

export default PerformanceSummary;